"use client"
import React, { useState, useRef, useMemo, useEffect } from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import Link from "next/link"
import gsap from "gsap"
import sanitizeHtml from "sanitize-html"
import { InteractiveHoverButton } from "../magicui/interactive-hover-button"

export default function TeamGallery({ members, slug }) {
  const router = useRouter()
  const previousSlugRef = useRef(null)
  const memberRef = useRef(null)
  const [isFading, setIsFading] = useState(false)

  const currentMember = useMemo(() => members.find(m => m.slug === slug), [slug, members])
  const otherMembers = useMemo(() => members.filter(m => m.slug !== slug), [slug, members])

  const handleMemberClick = member => {
    if (member.slug === slug) return // already selected

    previousSlugRef.current = slug
    setIsFading(true) // Start fading out

    setTimeout(() => {
      router.push(`/teammate/${member.slug}`, { scroll: false })
      // Reset fading state after navigation
      setTimeout(() => setIsFading(false), 100)
    }, 300) // Wait 300ms before changing
  }

  useEffect(() => {
    setIsFading(false) // Reset fading when slug changes

    if (memberRef.current) {
      gsap.fromTo(
        memberRef.current,
        {
          opacity: 0,
          y: 40
        },
        {
          opacity: 1,
          duration: 0.4,
          delay: 0.15,
          y: 0,
          ease: "power2.out"
        }
      )
    }
  }, [slug])

  return (
    <section>
      <div
        className="absolute top-0 left-0 w-full h-full opacity-40 -z-[1]"
        style={{
          backgroundImage: `url(${currentMember?._embedded["wp:featuredmedia"][0]?.source_url})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          filter: "blur(180px)"
        }}
      />
      <div className="py-8 md:container">
        <div className="max-w-screen-lg mx-auto">
          <Link href="/about#team" className="pb-6 block">
            <InteractiveHoverButton>Go back ←</InteractiveHoverButton>
          </Link>
          <div className="grid grid-cols-12 gap-4 lg:gap-12 bg-[#ffffff49]">
            {/* LEFT */}
            <div className="col-span-9 lg:col-span-7">
              {/* Main inside grid */}
              <div className="flex flex-col gap-4 p-x-4 lg:gap-x-8 py-10 w-full h-full">
                {/** Left */}
                <div className="px-4 lg:px-6 h-fit">
                  <div className={`bg-blue-400 relative rounded-2xl overflow-clip transition-all duration-300 ease-in-out cursor-pointer h-full max-w-[300px] max-h-[300px] aspect-[2/3] ${isFading ? "opacity-80" : "opacity-100"}`} style={{ viewTransitionName: `member-${currentMember?.slug}` }}>
                    <Image loading="eager" decoding="sync" src={currentMember?._embedded["wp:featuredmedia"][0]?.source_url} alt={currentMember?._embedded["wp:featuredmedia"][0]?.alt_text} fill className="object-cover overflow-clip md:rounded-lg transition-all duration-300 ease-in-out w-full h-full" />
                  </div>
                </div>
                {/** Right */}
                <div className="transition-opacity duration-300 ease-in-out px-4 lg:px-6" ref={memberRef}>
                  <h2 className={`mt-4 mb-2 ${isFading ? "opacity-50" : "opacity-100"} font-bold text-3xl lg:text-4xl xl:text-5xl`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentMember?.title?.rendered) }} />
                  <h3 className={`mb-3 xl:mb-8 ${isFading ? "opacity-50" : "opacity-100"} text-xl lg:text-2xl xl:text-3xl text-slate-500`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentMember?.excerpt?.rendered) }} />
                  <div className={`${isFading ? "opacity-50" : "opacity-100"} text-base max-w-full`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentMember?.content?.rendered) }} />
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-span-3 lg:col-span-5 max-h-screen overflow-y-scroll bg-slate-100 p-3 md:p-4 xl:p-8 justify-around">
              {otherMembers.map((member, i) => (
                <div key={member.slug} className="grid grid-cols-12 xl:gap-x-8 gap-y-2 lg:gap-y-8 mb-6 items-center cursor-pointer" onClick={() => handleMemberClick(member)}>
                  <figure className="col-span-12 lg:col-span-5 xl:col-span-4 overflow-hidden aspect-square relative max-h-full max-w-full md:max-h-20 md:max-w-20 lg:max-h-24 lg:max-w-24" style={{ viewTransitionName: `member-${member.slug}` }}>
                    <Image src={member._embedded["wp:featuredmedia"][0]?.source_url} alt={member?._embedded["wp:featuredmedia"][0]?.alt_text} fill className="object-cover rounded-lg md:rounded-xl lg:rounded-2xl mx-auto w-full" />
                  </figure>
                  <div className="col-span-12 lg:col-span-7 xl:col-span-8" style={{ viewTransitionName: `member-name-${member?.slug}` }}>
                    <h2 className="text-xs md:text-base xl:text-xl" dangerouslySetInnerHTML={{ __html: sanitizeHtml(member?.title?.rendered.split(" ")[0]) }} />
                    <h3 className="hidden md:inline-block text-xs md:text-sm xl:text-lg font-bold" dangerouslySetInnerHTML={{ __html: sanitizeHtml(member?.excerpt?.rendered, { ALLOWED_TAGS: [] }) }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
